import { Empty, List, Modal, Spin } from 'antd'
import { isEmpty } from 'lodash-es'
import { FunctionComponent, ReactNode, useState } from 'react'
import { useToggle } from 'react-use'
import styled, { useTheme } from 'styled-components'
import {
  useDeleteNoteMutation,
  useFetchNoteQuery,
} from '@/api/workspace-api/directory-api'
import { InitialLoadingBlock } from '@/components/common/initial-loading-block'
import Actions from '@/components/kol/common/kol-cooperation-detail-drawer/contact-info/actions'
import AddNoteModal from '@/components/kol/common/kol-cooperation-detail-drawer/notes/add-note-modal'
import EditNoteModal from '@/components/kol/common/kol-cooperation-detail-drawer/notes/edit-note-modal'
import useKolInfo from '@/components/kol/hooks/use-kol-info'
import { DataWrapper } from '@/components/kol/irm/common/data-wrapper'
import { TextWrapper } from '@/components/kol/irm/common/text-wrapper'
import { Time } from '@/components/kol/irm/common/time'
import { Wrapper } from '@/components/kol/irm/common/wrapper'
import { InfoWrapper } from '@/components/kol/irm/info-wrapper'
import { AvatarImage } from '@/components/workspace/avatar-image'
import { useAuth } from '@/hooks/use-auth'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Note } from '@/types/schema/directory/kol-management'
import { ConfigureCollaborationNotesProperties, ampli } from '@/utils/ampli'

const { confirm } = Modal

export type TriggerNotesEvent = (props: {
  action: ConfigureCollaborationNotesProperties['action']
  id?: number
  fieldValue?: ConfigureCollaborationNotesProperties['fieldValue']
}) => void

interface NoteListProps {
  kolId: string
  from?: ConfigureCollaborationNotesProperties['from']
}

const NoteList: FunctionComponent<NoteListProps> = ({ kolId, from }) => {
  const { formatMessage, formatDate } = useIntl()
  const theme = useTheme()
  const [displayAddModal, setDisplayAddModal] = useToggle(false)
  const [displayEditModal, setDisplayEditModal] = useToggle(false)
  const [note, setNote] = useState<Note>()
  const { userInfo } = useAuth()
  const { kolInfo } = useKolInfo(kolId)
  const { data: notes, isFetching } = useFetchNoteQuery({ kolId })
  const [deleteNote] = useDeleteNoteMutation()

  const toggleAddModal = (): void => {
    setDisplayAddModal(!displayAddModal)
  }

  const toggleEditModal = (): void => {
    setDisplayEditModal(!displayEditModal)
  }

  const onDelete = (id: number): void => {
    confirm({
      title: formatMessage({ id: 'irm:delete_cooperation_note_modal_title' }),
      content: formatMessage({
        id: 'irm:delete_cooperation_note_modal_content',
      }),
      okText: formatMessage({ id: 'general:btn_text_confirm' }),
      onOk() {
        deleteNote({ id })
      },
    })
  }

  const triggerNotesEvent: TriggerNotesEvent = ({
    action,
    id,
    fieldValue,
  }): void => {
    ampli.configureCollaborationNotes({
      action,
      from,
      kolId,
      kolName: kolInfo?.name,
      isClickedFromDrawer: true,
      fieldValue,
      noteId: id,
    })
  }

  const onEdit = (n: Note): void => {
    setNote(n)
    toggleEditModal()
  }

  return (
    <InfoWrapper
      buttonPlacement='right'
      title={formatMessage({ id: 'autoreport:chart_field_add_btn' })}
      onClick={toggleAddModal}
    >
      {notes !== undefined ? (
        <Spin spinning={isFetching}>
          {isEmpty(notes) ? (
            <Empty
              description={formatMessage({
                id: 'autoreport:chart_label_empty_data',
              })}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <List
              dataSource={notes}
              itemLayout='horizontal'
              renderItem={(note: Note): ReactNode => {
                const name = note.User.Name
                return (
                  <List.Item>
                    <ListContentWrapper>
                      <DataWrapper>
                        <TextWrapper>
                          <DescriptionInfoWrapper>
                            <AvatarImage
                              name={name}
                              size={20}
                              style={{
                                backgroundColor: theme.colors.brand.background,
                                color: theme.colors.brand.secondary,
                                fontSize: '10px',
                                fontWeight: '700',
                                alignContent: 'center',
                              }}
                            />
                            <Name>{name}</Name>
                            <TimeText>
                              {formatDate(note.UpdatedAt, 'dateTimeLong')}
                            </TimeText>
                            {note.IsEdited && (
                              <TimeText>
                                {formatMessage({ id: 'irm:lable_edited' })}
                              </TimeText>
                            )}
                          </DescriptionInfoWrapper>
                          <Description>{note.Note}</Description>
                        </TextWrapper>
                      </DataWrapper>
                      <Actions
                        onDelete={(): void => {
                          onDelete(note.ID)
                          triggerNotesEvent({
                            action: 'delete',
                            id: note.ID,
                            fieldValue: note.Note,
                          })
                        }}
                        onEdit={
                          note.UserID === Number(userInfo?.id)
                            ? (): void => onEdit(note)
                            : undefined
                        }
                      />
                    </ListContentWrapper>
                  </List.Item>
                )
              }}
            />
          )}
        </Spin>
      ) : (
        <InitialLoadingBlock />
      )}
      <AddNoteModal
        kolId={kolId}
        open={displayAddModal}
        toggleModal={toggleAddModal}
        triggerNotesEvent={triggerNotesEvent}
      />
      <EditNoteModal
        note={note}
        open={displayEditModal}
        toggleModal={toggleEditModal}
        triggerNotesEvent={triggerNotesEvent}
      />
    </InfoWrapper>
  )
}

const Name = styled.span`
  font-size: 13px;
  line-height: 140%;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const TimeText = styled(Time)`
  font-size: 12px;
  line-height: 140%;
  color: ${({ theme }): string => theme.colors.text.tooltip};
`

const Description = styled.div`
  font-size: 14px;
  line-height: 140%;
  color: ${({ theme }): string => theme.colors.text.primary};
  white-space: pre-line;
  margin: 8px 0 0 0;
  overflow-wrap: anywhere;
`

const DescriptionInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const ListContentWrapper = styled(Wrapper)`
  gap: 12px;
`

export default NoteList
